export type AttractionTuning = {
  /** Peak attraction magnitude before clamp. */
  attraction: number;
  maxForce: number;
  /** Beyond this distance, force is effectively zero. */
  interactionRadius: number;
  /** Soften force inside this radius (avoid singularity / sticky feel). */
  innerSoftRadius: number;
  /** Distance at which force peaks. */
  peakForceDistance: number;
  torqueStrength: number;
};

export type ForceSample = {
  fx: number;
  fy: number;
  strength01: number;
  distance: number;
};

/**
 * Soft local gravity well:
 * - dead outside interactionRadius
 * - rises through the middle band toward peakForceDistance
 * - softens again inside innerSoftRadius
 */
export function attractionForce(
  bodyX: number,
  bodyY: number,
  pointerX: number,
  pointerY: number,
  tuning: AttractionTuning,
): ForceSample {
  const dx = pointerX - bodyX;
  const dy = pointerY - bodyY;
  const dist = Math.hypot(dx, dy);
  const curve = forceCurve01(dist, tuning);
  const strength = Math.min(tuning.maxForce, tuning.attraction * curve);
  if (dist < 1e-4 || strength <= 0) {
    return { fx: 0, fy: 0, strength01: 0, distance: dist };
  }
  const inv = 1 / dist;
  return {
    fx: dx * inv * strength,
    fy: dy * inv * strength,
    strength01: curve,
    distance: dist,
  };
}

export function forceCurve01(dist: number, tuning: AttractionTuning): number {
  const outer = Math.max(tuning.interactionRadius, 1);
  const peak = clamp(tuning.peakForceDistance, 1, outer);
  const soft = clamp(tuning.innerSoftRadius, 0, peak);

  if (dist >= outer) return 0;

  if (dist >= peak) {
    return 1 - smoothstep(peak, outer, dist);
  }

  if (dist >= soft) {
    return 1;
  }

  const near = soft > 0 ? dist / soft : 1;
  return lerp(0.35, 1, smoothstep(0, 1, near));
}

/**
 * Torque mostly from tangential pointer motion relative to body.
 * Radial motion (toward/away) contributes little.
 */
export function torqueFromPointer(
  bodyX: number,
  bodyY: number,
  pointerX: number,
  pointerY: number,
  pointerVx: number,
  pointerVy: number,
  torqueStrength: number,
): number {
  const rx = pointerX - bodyX;
  const ry = pointerY - bodyY;
  const rLen = Math.hypot(rx, ry);
  if (rLen < 10) return 0;

  const inv = 1 / rLen;
  const tx = -ry * inv;
  const ty = rx * inv;
  const tangentialSpeed = pointerVx * tx + pointerVy * ty;

  // Useful mid-band emphasis; weak when extremely close or far.
  const mid = Math.exp(-Math.pow((rLen - 90) / 110, 2));
  return tangentialSpeed * mid * 0.018 * torqueStrength;
}

/**
 * Antagonist pulse: pushes the body away from a circle center.
 * Strongest near the rim, soft inside, zero outside.
 */
export function repulsionForce(
  bodyX: number,
  bodyY: number,
  originX: number,
  originY: number,
  radius: number,
  strength: number,
): ForceSample {
  const dx = bodyX - originX;
  const dy = bodyY - originY;
  const dist = Math.hypot(dx, dy);
  if (radius <= 1 || dist >= radius || strength <= 0) {
    return { fx: 0, fy: 0, strength01: 0, distance: dist };
  }
  // Peak near mid-radius; soften at center and fall off at edge.
  const t = dist / radius;
  const curve = Math.sin(Math.PI * t); // 0 at center & edge, 1 mid
  const mag = strength * curve;
  if (dist < 1e-3) {
    // Arbitrary push if sitting on the origin.
    return { fx: mag, fy: 0, strength01: curve, distance: dist };
  }
  const inv = 1 / dist;
  return {
    fx: dx * inv * mag,
    fy: dy * inv * mag,
    strength01: curve,
    distance: dist,
  };
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge1 <= edge0) return x < edge0 ? 0 : 1;
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
